import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "./Navbar";
import Hero from "./Hero";
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import ForceChangePassword from "./pages/ForceChangePassword";
import ManagementDashboard from "./pages/management/ManagementDashboard";
import CreateUserPage from "./pages/CreateUserPage";
// Public visitor flow: browse rooms, sign up, reserve.
import Reserve from "./pages/public/Reserve";
import GuestSignup from "./pages/public/GuestSignup";
import GuestLogin from "./pages/public/GuestLogin";
import MyReservations from "./pages/public/MyReservations";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import InstallButton from "./components/InstallButton";
import FirstVisitInstallPrompt from "./components/FirstVisitInstallPrompt";
import { FacilityProvider, useFacility } from "./context/FacilityContext";
import { SettingsProvider } from "./context/SettingsContext";
import { useInactivityLogout } from "./hooks/useInactivityLogout";

// Stamp every request with the cached user id so the backend can verify
// the caller's role without us shipping a JWT.  This mirrors the simple
// header-based auth used by the TotalCare backend.
axios.interceptors.request.use((config) => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.id) {
      config.headers = config.headers || {};
      config.headers["x-user-id"] = user.id;
    }
    // Visitors sign in with a separate "guest" account.  Both headers can
    // be present without conflict — each backend route reads only the one
    // it cares about.
    const guest = JSON.parse(localStorage.getItem("guest") || "null");
    if (guest?.id) {
      config.headers = config.headers || {};
      config.headers["x-guest-id"] = guest.id;
    }
  } catch (_) {}
  return config;
});

// Response interceptor — when the backend returns 401 (e.g. the user
// was deactivated mid-session, or the global gate rejected the request)
// clear the cached session and bounce the operator to /login.  Avoids
// the dashboard sitting there showing stale data with every request
// silently failing.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      try {
        if (localStorage.getItem("user")) {
          localStorage.setItem("session_expired", "auth");
          localStorage.removeItem("user");
        }
      } catch (_) {}
      // Don't hard-redirect from /login itself (avoid loops).
      if (typeof window !== "undefined" && !/\/login$/.test(window.location.pathname)) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  }
);

// Mounts the 10-minute inactivity timer for any logged-in operator.
// Lives inside BrowserRouter so it can navigate on logout.  Renders
// nothing.
function InactivityGuard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  useInactivityLogout({
    enabled: !!user,
    onLogout: () => navigate("/login", { replace: true }),
  });
  return null;
}

// Until the facility row exists, the whole product is unusable: even the
// marketing pages quote the hotel name.  This guard wraps every public route
// and bounces the user to /setup when they show up before initial setup.
function RequireSetup({ children }) {
  const { configured, loading } = useFacility();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#1a110a", color: "#c8a05a", fontFamily: "'Jost', sans-serif", letterSpacing: ".18em", textTransform: "uppercase", fontSize: 12 }}>
        Loading…
      </div>
    );
  }
  if (!configured && location.pathname !== "/setup") {
    return <Navigate to="/setup" replace />;
  }
  return children;
}

function HomePage() {
  return (
    <>
      <Navbar />
      <Hero />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <FacilityProvider>
       <SettingsProvider>
        {/* PWA install affordances — rendered globally so they appear on
            every route (marketing, setup, login, management).  Both render
            null when the app is already installed or the browser hasn't
            surfaced an install prompt. */}
        <InstallButton />
        <FirstVisitInstallPrompt />
        {/* Idle-logout watchdog: kicks the operator back to /login after
            10 minutes with no mouse / keyboard activity. */}
        <InactivityGuard />
        <Routes>
          {/* /setup is the only route that bypasses the RequireSetup guard. */}
          <Route path="/setup" element={<SetupPage />} />

          <Route path="/" element={<RequireSetup><HomePage /></RequireSetup>} />
          <Route path="/login" element={<RequireSetup><LoginPage /></RequireSetup>} />

          {/* ── Public visitor flow ─────────────────────────────────────
              Browseable without auth.  /reserve lets guests pick a room;
              the booking action itself requires a signed-in guest. */}
          <Route path="/reserve"        element={<RequireSetup><Reserve /></RequireSetup>} />
          <Route path="/guest/signup"   element={<RequireSetup><GuestSignup /></RequireSetup>} />
          <Route path="/guest/login"    element={<RequireSetup><GuestLogin /></RequireSetup>} />
          <Route path="/my-reservations" element={<RequireSetup><MyReservations /></RequireSetup>} />
          <Route
            path="/first-login"
            element={
              <RequireSetup>
                <ForceChangePassword />
              </RequireSetup>
            }
          />
          <Route
            path="/management"
            element={
              <RequireSetup>
                <ProtectedRoute>
                  <ManagementDashboard />
                </ProtectedRoute>
              </RequireSetup>
            }
          />
          <Route
            path="/admin/create-user"
            element={
              <RequireSetup>
                <AdminRoute>
                  <CreateUserPage />
                </AdminRoute>
              </RequireSetup>
            }
          />
        </Routes>
       </SettingsProvider>
      </FacilityProvider>
    </BrowserRouter>
  );
}
